import type { ManifestAction, MessageContent } from './protocol/index.js'
import type { ChatStore, RenderMessage } from './store.js'
import { CSS } from './renderer.styles.js'

export interface WidgetConfig {
  subject?:      { title?: string; subtitle?: string; tags?: string[]; status?: string; ownerLabel?: string }
  quickReplies?: string[]
  accent?:       string
  /** Secondary accent — the guest's OWN bubble + send button. Defaults to the
   *  design blue; if omitted while `accent` is set, follows `accent` so a single
   *  accent override re-themes cohesively. */
  accent2?:      string
  /** Colour scheme. Default 'light'. 'auto' follows the OS (prefers-color-scheme);
   *  'dark'/'light' force it. Dark is never auto-applied unless the host opts in. */
  theme?:        'auto' | 'light' | 'dark'
  /** Load the brand webfonts (Baloo 2 + Nunito). Default true; set false for
   *  strict-CSP / privacy-sensitive hosts (falls back to the system stack). */
  webfont?:      boolean
  /** Identified user info — shown as the guest avatar/name in the widget header. */
  userInfo?:     { name?: string; avatar?: string }
  /** i18n string overrides */
  i18n?:         { placeholder?: string; send?: string; offline?: string; poweredBy?: string; online?: string; away?: string; aiAssistant?: string; resolved?: string; reopen?: string; sources?: string; helpful?: string; notHelpful?: string; translate?: string; original?: string; showOriginal?: string; translateUnavailable?: string; back?: string; sendMessage?: string; conversation?: string; typing?: string }

}

export interface RendererHandlers {
  onSend(text: string): void
  onAttach?(file: File): void
  onInvoke(actionId: string, inputs?: Record<string, unknown>): void
  onTyping(isTyping: boolean, preview?: string): void
  onReadUpTo(seq: number): void
  onReact?(messageId: string, emoji: string, remove: boolean): void
  onCsat?(score: number): void
  onLoadMore?(): void
  onEdit?(messageId: string, newText: string): void
  onDelete?(messageId: string): void
  /** Pre-chat qualification submitted (values keyed by field; topic/callback included). */
  onPreChat?(values: { name?: string; email?: string; phone?: string; topic?: string; callback?: boolean }): void
  /** KB deflection: the guest is typing their FIRST message — look up articles. */
  onDeflectQuery?(q: string): void
  /** Translate a message's text for display. Return null if unavailable —
   *  the renderer shows a brief "unavailable" hint and leaves the original. */
  onTranslate?(text: string): Promise<string | null>
  /** Stack navigation (chat-app surfaces): when set, the header shows a back
   *  chevron on the left that calls this — tap a conversation → chatroom →
   *  back → list, like a native messaging app. Omit for a standalone widget,
   *  which has nothing to go "back" to. */
  onBack?(): void
}

const STYLE_ID = 'objectchat-widget-styles'
const REACTION_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🙏']
/** Width below which the widget switches to compact (touch-friendly) sizing. */
const COMPACT_BREAKPOINT = 400
// Inline, dependency-free send glyph — `currentColor` follows the button text
// colour; no emoji/font dependency so it renders identically across platforms.
const SEND_ICON_SVG =
  '<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
  '<path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round"/></svg>'
// CSS lives in renderer.styles.ts (single source of truth for .ocw styling).

function injectStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return
  const s = document.createElement('style'); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s)
}

/** Load the brand webfonts (Baloo 2 + Nunito) once. The design's identity is its
 *  rounded type — without this the widget falls back to system fonts and looks
 *  generic. Injected as a <link> so a strict host CSP that blocks it degrades
 *  gracefully to the system stack. Opt out with `webfont: false`. */
const FONT_ID = 'ocw-webfont'
function injectFonts(): void {
  if (typeof document === 'undefined' || document.getElementById(FONT_ID)) return
  const l = document.createElement('link')
  l.id = FONT_ID; l.rel = 'stylesheet'
  l.href = 'https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700&family=Nunito:wght@400;500;600;700&display=swap'
  document.head.appendChild(l)
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag); if (cls) n.className = cls; if (text !== undefined) n.textContent = text; return n
}

/** Scheme allow-list for any URL that arrives in message CONTENT.
 *
 *  The linkifier below is safe by construction — its regex only ever matches
 *  `http(s)://`. These other sinks are not: an attachment's `url` and an
 *  appointment's calendar links are taken from the message as delivered, and a
 *  `javascript:` value in one of them becomes script execution in the HOST
 *  page's origin the moment a visitor clicks. The server validates what it
 *  writes, but the widget renders into someone else's website and should not
 *  depend on that.
 *
 *  Returns null for anything not allow-listed, so callers can degrade to plain
 *  text rather than rendering a link that does something else. */
function safeUrl(raw: string | undefined, opts: { allowData?: RegExp } = {}): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  // Resolved against the document so relative URLs (a legitimate attachment
  // path) keep working, and so protocol-relative and obfuscated forms are
  // normalized before the check rather than after.
  let url: URL
  try { url = new URL(trimmed, typeof location !== 'undefined' ? location.href : 'https://localhost/') }
  catch { return null }
  if (url.protocol === 'http:' || url.protocol === 'https:' || url.protocol === 'mailto:' || url.protocol === 'tel:') return url.href
  // Calendar downloads are legitimately data: URIs; anything else with that
  // scheme (notably data:text/html) is not.
  if (opts.allowData && url.protocol === 'data:' && opts.allowData.test(trimmed)) return trimmed
  return null
}

// Append plain text with any http(s) URLs turned into clickable links (safe:
// text goes through textContent / createTextNode — no innerHTML). Returns a Text
// node so callers that guard on "has text" keep working.
function linkifyInto(parent: HTMLElement, text: string): Text {
  const urlRe = /(https?:\/\/[^\s]+)/g
  let last = 0
  let first: Text | null = null
  let m: RegExpExecArray | null
  while ((m = urlRe.exec(text)) !== null) {
    if (m.index > last) { const tn = document.createTextNode(text.slice(last, m.index)); parent.append(tn); first ??= tn }
    let href = m[0]; let trail = ''
    // Don't swallow trailing sentence punctuation into the URL.
    const mt = /[).,!?;:]+$/.exec(href); if (mt) { trail = mt[0]; href = href.slice(0, -trail.length) }
    const a = document.createElement('a')
    a.href = href; a.target = '_blank'; a.rel = 'noopener noreferrer'; a.textContent = href
    a.style.cssText = 'color:inherit;text-decoration:underline;word-break:break-all'
    parent.append(a)
    if (trail) parent.append(document.createTextNode(trail))
    last = m.index + m[0].length
  }
  const tail = document.createTextNode(text.slice(last)); parent.append(tail); first ??= tail
  return first
}
function fmtTime(ts: number): string {
  try { return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) } catch { return '' }
}
function contentText(c: MessageContent): string {
  switch (c.kind) {
    case 'text':        return c.text
    case 'system':      return typeof c.data?.['message'] === 'string' ? String(c.data['message']) : c.event
    case 'card':        return [c.title, c.body].filter(Boolean).join(' — ')
    case 'attachment':  return c.name ?? c.url
    case 'form':        return c.prompt
    case 'appointment': return `📅 ${c.title} — ${new Date(c.startIso).toLocaleString()}`
    case 'products':    return c.items.map(i => i.title).join(' · ')
  }
}

/** Renders a ChatStore into a host element in the Image-1 layout: header →
 *  subject card → action chips → chat → quick replies → input. Self-injects its
 *  stylesheet so it looks right wherever it mounts. Accent comes from the
 *  dashboard-authored profile theme (via the manifest), falling back to config. */
export class Renderer {
  private readonly scroll:  HTMLElement
  private readonly chips:   HTMLElement
  private readonly typing:  HTMLElement
  private readonly quick:   HTMLElement
  private suggestedKey = ''
  private readonly formHost: HTMLElement
  private readonly csatPanel: HTMLElement
  private readonly awayNotice: HTMLElement
  private readonly headStatus: HTMLElement
  private readonly reopenBtn: HTMLButtonElement
  private readonly preChatPanel: HTMLElement
  private readonly deflectPanel: HTMLElement
  private preChatBuilt = false
  private preChatDone = false
  private readonly footer: HTMLElement
  private readonly input:   HTMLTextAreaElement
  private readonly subjectCard: HTMLElement
  private readonly e2eBadge: HTMLElement
  private readonly statusBadge: HTMLElement
  private readonly headerName: HTMLElement
  private readonly connStatus: HTMLElement
  private typingTimer: ReturnType<typeof setTimeout> | null = null
  private csatSubmitted = false
  private readonly sendBtn: HTMLButtonElement
  /** Container-driven responsive sizing — toggles .ocw-compact (see CSS note). */
  private compactObserver: ResizeObserver | null = null
  private storeRef: ChatStore | null = null
  private readonly cfgAccent2: string | undefined
  private scrollCleanup: (() => void) | null = null

  /** Returns the scroll container so history.ts can attach scroll listeners. */
  getScrollEl(): HTMLElement | null { return this.scroll }

  /** Registers a cleanup fn removed on destroy() to prevent listener leaks. */
  setScrollCleanup(fn: () => void): void {
    this.scrollCleanup?.()
    this.scrollCleanup = fn
  }

  // ── Live translation ──────────────────────────────────────────────────-
  private readonly translationCache = new Map<string, string>()
  private readonly showingTranslation = new Set<string>()
  private readonly autoTranslated = new Set<string>()   // system/fixed msgs we've auto-translated


  /** Last seq the guest has seen per conversationId — used to compute unread badges. */


  constructor(
    private readonly root: HTMLElement,
    private readonly me: string,
    private readonly h: RendererHandlers,
    private readonly cfg: WidgetConfig = {},
  ) {
    injectStyles()
    if (cfg.webfont !== false) injectFonts()
    // Clear any previous widget content on this element before building.
    // This is the last line of defence against double-mounts: even if mount()
    // is called twice on the same element (React StrictMode, HMR, caller bug),
    // the second Renderer wipes the first one's DOM so only one UI is visible.
    root.replaceChildren()
    root.classList.add('ocw')
    if (cfg.accent) root.style.setProperty('--ocw-accent', cfg.accent)
    // Two-accent model: explicit accent2 wins; else if only accent is set, accent2
    // follows it (single-token retheme); else the CSS defaults (indigo + blue) hold.
    const accent2 = cfg.accent2 ?? cfg.accent
    if (accent2) root.style.setProperty('--ocw-accent2', accent2)
    this.cfgAccent2 = cfg.accent2
    // Colour scheme is a deliberate choice, never auto-detected by default:
    // unset → 'light'. 'auto' is an explicit opt-in that follows the OS via the
    // prefers-color-scheme media query (which is scoped to [data-theme="auto"]);
    // 'dark'/'light' force the scheme regardless of the OS.
    root.dataset.theme = cfg.theme ?? 'light'

    // Header
    const head = el('div', 'ocw-head')
    // Stack navigation: a back chevron returns to the conversation list. Only
    // shown when the host wired onBack (chat-app surfaces) — a standalone
    // support widget has no list to go back to.
    if (this.h.onBack) {
      const back = el('button', 'ocw-back', '‹') as HTMLButtonElement
      back.type = 'button'
      back.setAttribute('aria-label', cfg.i18n?.back ?? 'Back')
      back.addEventListener('click', () => this.h.onBack!())
      head.append(back)
    }
    // Header identity is the party the guest is talking TO (the brand/room), so
    // the avatar mirrors the header name — a brand monogram (e.g. "NM"), never a
    // generic person glyph. Hidden entirely when there's no brand name to show.
    const brandName = cfg.subject?.ownerLabel ?? cfg.subject?.title ?? ''
    const avatarEl = el('div', 'ocw-avatar')
    if (brandName.trim()) {
      avatarEl.textContent = brandName.trim()[0]!.toUpperCase()
      head.append(avatarEl)
    }
    const hm = el('div', 'ocw-head-main')
    this.headerName = el('div', 'ocw-head-name', cfg.subject?.ownerLabel ?? cfg.subject?.title ?? '')
    hm.append(this.headerName)
    this.headStatus = el('div', 'ocw-head-status'); this.headStatus.style.display = 'none'
    hm.append(this.headStatus)
    if (cfg.subject?.subtitle) hm.append(el('div', 'ocw-head-meta', cfg.subject.subtitle))
    head.append(hm)
    this.statusBadge = el('span', 'ocw-badge', cfg.subject?.status ?? '')
    if (!cfg.subject?.status) this.statusBadge.style.display = 'none'
    head.append(this.statusBadge)
    this.e2eBadge = el('span', 'ocw-e2e', '🔒 E2E'); this.e2eBadge.style.display = 'none'; head.append(this.e2eBadge)
    this.connStatus = el('span', 'ocw-conn-status'); this.connStatus.style.display = 'none'; head.append(this.connStatus)

    head.append(el('button', 'ocw-menu', '⋯'))

    // Action chips (filled in render)
    this.chips = el('div', 'ocw-chiprow')

    // Scroll area with optional subject card + messages
    this.scroll = el('div', 'ocw-scroll')
    // Screen readers: the transcript is a live log. `polite` so an incoming bot
    // or agent reply is announced without interrupting what the guest is typing;
    // `additions` so re-rendering an existing bubble (reactions, edits, the
    // translate toggle) is not re-read. Without this a non-sighted guest gets no
    // signal that anyone answered.
    this.scroll.setAttribute('role', 'log')
    this.scroll.setAttribute('aria-live', 'polite')
    this.scroll.setAttribute('aria-relevant', 'additions')
    this.scroll.setAttribute('aria-label', cfg.i18n?.conversation ?? 'Conversation')
    this.subjectCard = el('div', 'ocw-subject')
    this.typing = el('div', 'ocw-typing')
    const typingBubble = el('div', 'ocw-typing-bubble')
    typingBubble.append(el('div', 'ocw-typing-dot'), el('div', 'ocw-typing-dot'), el('div', 'ocw-typing-dot'))
    this.typing.append(typingBubble)
    this.quick = el('div', 'ocw-quick')
    this.buildQuick(cfg.quickReplies ?? [])

    // Input
    this.formHost = el('div', 'ocw-form-host')
    this.csatPanel = el('div', 'ocw-csat'); this.csatPanel.style.display = 'none'
    // Away notice (outside office hours). INFORMATIONAL only — it never blocks
    // the composer: the message is delivered either way and an agent replies
    // when they're back. (It used to be a card that replaced the composer with
    // a name/email/message form; guests could not simply chat.)
    this.awayNotice = el('div', 'ocw-away'); this.awayNotice.style.display = 'none'
    this.preChatPanel = el('div', 'ocw-prechat'); this.preChatPanel.style.display = 'none'
    this.deflectPanel = el('div', 'ocw-deflect'); this.deflectPanel.style.display = 'none'
    this.input = el('textarea', undefined); this.input.rows = 1
    // i18n.placeholder was documented in MountOptions but never actually
    // applied — the composer always said "Message…" regardless.
    this.input.placeholder = cfg.i18n?.placeholder ?? 'Message…'
    const sendBtn = el('button', 'ocw-sendbtn') as HTMLButtonElement
    sendBtn.type = 'button'
    const sendLabel = cfg.i18n?.send
    if (sendLabel) {
      // Text label → auto-width pill (see the .ocw-sendbtn-label CSS note).
      sendBtn.textContent = sendLabel
      sendBtn.classList.add('ocw-sendbtn-label')
      sendBtn.setAttribute('aria-label', sendLabel)
    } else {
      // Default: inline SVG paper plane — renders identically on every
      // platform (the old '➤' text glyph varied per OS font).
      sendBtn.innerHTML = SEND_ICON_SVG
      sendBtn.setAttribute('aria-label', cfg.i18n?.sendMessage ?? 'Send message')
    }
    // The :disabled style existed but nothing ever set the state — send is
    // inactive until there's something to send, like every mainstream chat UI.
    sendBtn.disabled = true
    this.sendBtn = sendBtn
    sendBtn.addEventListener('click', () => this.flushSend())
    this.input.addEventListener('input', () => {
      this.sendBtn.disabled = this.input.value.trim().length === 0
      this.autoGrowInput()
      // Deflection fires only for the FIRST message of an empty conversation —
      // once a thread exists, suggestions would just be noise.
      if (this.storeRef && !this.storeRef.messages().some(m => m.senderRole === 'guest')) this.h.onDeflectQuery?.(this.input.value)
      else this.hideDeflection()
    })
    this.input.addEventListener('keydown', (e) => {
      // Guard against IME composition (Korean/Japanese/Chinese input): while
      // the user is selecting a candidate from the IME's suggestion list,
      // pressing Enter to CONFIRM the candidate also fires a keydown with
      // key === 'Enter'. Without this check, that confirmation keystroke was
      // being treated as "send the message" — firing early with a partial
      // composition, and then firing again on the real Enter press with
      // whatever text was left, producing two bubbles for one message
      // (e.g. typing "음식" sends "음식" then "식").
      // e.isComposing covers most browsers; keyCode 229 is the long-standing
      // fallback for browsers/IMEs that don't set isComposing reliably.
      if (e.isComposing || e.keyCode === 229) return
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); this.flushSend() } else this.signalTyping()
    })

    const attachBtn = el('button', 'ocw-attach', '📎'); attachBtn.title = 'Attach image or file'
    const fileInput = document.createElement('input'); fileInput.type = 'file'
    fileInput.accept = 'image/*,.pdf,.txt,.doc,.docx'; fileInput.style.display = 'none'
    attachBtn.addEventListener('click', () => fileInput.click())
    fileInput.addEventListener('change', () => { if (fileInput.files?.[0] && this.h.onAttach) this.h.onAttach(fileInput.files[0]); fileInput.value = '' })

    const inputRow = el('div', 'ocw-input'); inputRow.append(attachBtn, fileInput, this.input, sendBtn)

    const footer = el('div', 'ocw-footer')
    // i18n.poweredBy: if the caller provides it, treat as plain text — no innerHTML.
    // Only our own hardcoded default renders the anchor as HTML.
    if (cfg.i18n?.poweredBy !== undefined) {
      footer.textContent = cfg.i18n.poweredBy
    } else {
      footer.innerHTML = 'Powered by <a href="https://relay.paramms.com" target="_blank" rel="noopener">Relay</a>'
    }
    this.footer = footer

    this.reopenBtn = el('button', 'ocw-reopen', this.cfg.i18n?.reopen ?? 'Reopen conversation') as HTMLButtonElement
    this.reopenBtn.type = 'button'
    this.reopenBtn.addEventListener('click', () => {
      this.reopenBtn.style.display = 'none'
      const ta = this.root.querySelector('.ocw-input textarea') as HTMLTextAreaElement | null
      ta?.focus()
    })
    root.append(head, this.chips, this.scroll, this.typing, this.quick, this.formHost, this.csatPanel, this.preChatPanel, this.deflectPanel, this.awayNotice, inputRow, this.reopenBtn, this.footer)

    // Container-driven responsive sizing: compact below COMPACT_BREAKPOINT of
    // the widget's OWN width. Covers phones AND narrow desktop embeds — a
    // viewport media query can't see the container. Guarded: jsdom/tests and
    // very old runtimes have no ResizeObserver; they keep desktop sizing.
    const applyCompact = (w: number): void => { root.classList.toggle('ocw-compact', w > 0 && w < COMPACT_BREAKPOINT) }
    applyCompact(root.clientWidth)
    if (typeof ResizeObserver !== 'undefined') {
      this.compactObserver = new ResizeObserver((entries) => {
        const w = entries[0]?.contentRect.width ?? root.clientWidth
        applyCompact(w)
      })
      this.compactObserver.observe(root)
    }
  }

  /** Call when the widget is unmounted. Disconnects scroll listeners and clears timers. */
  destroy(): void {
    this.scrollCleanup?.()
    this.scrollCleanup = null
    if (this.typingTimer) { clearTimeout(this.typingTimer); this.typingTimer = null }
    this.compactObserver?.disconnect()
    this.compactObserver = null
  }


  private flushSend(): void {
    this.hideDeflection()
    const text = this.input.value.trim()
    if (!text) return
    this.input.value = ''
    this.sendBtn.disabled = true
    this.autoGrowInput()   // collapse back to one row
    this.h.onTyping(false)
    this.h.onSend(text)
  }

  /** Grow the composer with its content (up to the CSS max-height), collapse
   *  when cleared. scrollHeight is 0 in non-layout environments (jsdom) —
   *  skip there so tests and SSR-ish mounts are unaffected. */
  private autoGrowInput(): void {
    this.input.style.height = 'auto'
    const sh = this.input.scrollHeight
    if (sh > 0) this.input.style.height = `${Math.min(sh, 120)}px`
    else this.input.style.removeProperty('height')
  }
  private signalTyping(): void {
    const preview = this.input.value.trim().slice(0, 100) || undefined
    this.h.onTyping(true, preview)
    if (this.typingTimer) clearTimeout(this.typingTimer)
    this.typingTimer = setTimeout(() => this.h.onTyping(false), 2000)
  }

  /** A3.3 — per-chatroom bot identity from the manifest; relabels bot bubbles. */
  setBotName(name: string): void {
    this.cfg.i18n = { ...(this.cfg.i18n ?? {}), aiAssistant: name }
  }

  /** (Re)build the strip of tappable prompts above the composer. Each tap sends
   *  the prompt as the guest's message and hides the strip. */
  private buildQuick(items: string[]): void {
    this.quick.replaceChildren()
    for (const q of items) {
      const b = el('button', undefined, q)
      b.type = 'button'
      b.addEventListener('click', () => {
        this.h.onSend(q)
        this.quick.style.display = 'none'
      })
      this.quick.append(b)
    }
  }

  render(store: ChatStore): void {
    this.storeRef = store
    if (store.accent) {
      this.root.style.setProperty('--ocw-accent', store.accent)
      this.root.style.setProperty('--ocw-accent2', this.cfgAccent2 ?? store.accent)
    }
    this.e2eBadge.style.display = store.e2e ? 'inline-flex' : 'none'
    this.buildSubjectCard(store)
    // Header: when a subject is attached, show ownerLabel ("Seller", "Host")
    // or nothing — the subject card below carries the identity.
    // Without a subject, the header is already set to cfg.subject?.ownerLabel
    // or "Chat" from the constructor — don't overwrite it with the domain name
    // which would duplicate the subject card title or clutter a plain chat.
    if (store.subject) {
      const ownerLabel = this.cfg.subject?.ownerLabel
      if (ownerLabel) this.headerName.textContent = ownerLabel
      // else leave constructor default ("Chat")
    }
    // Without a subject: leave header as-is (set once in constructor)

    // Quick replies are a first-touch affordance ("Is this still available?").
    // Quick replies / suggested questions belong only BEFORE the guest's first
    // message — once they have written anything, hide them, and keep them
    // hidden on every re-render (returning to the widget, reload, back-nav).
    // Keyed on a GUEST message, not on any message: a chatroom welcome message
    // is a system message and must not suppress the very prompts it invites.
    // Host-configured quickReplies win; otherwise the manifest's server-
    // authored suggestedQuestions fill the same strip.
    if (!this.cfg.quickReplies?.length) {
      const key = store.suggestedQuestions.join('\u0000')
      if (key !== this.suggestedKey) { this.suggestedKey = key; this.buildQuick(store.suggestedQuestions) }
    }
    const guestHasWritten = store.messages().some(m => m.senderId === this.me)
    this.quick.style.display = !guestHasWritten && this.quick.childElementCount > 0 ? 'flex' : 'none'

    // Action chips from the manifest (filtered by state in the store)
    this.chips.replaceChildren()
    const actions = store.visibleActions()
    this.chips.style.display = actions.length ? 'flex' : 'none'
    for (const a of actions) this.chips.append(this.chipEl(a))

    // Messages
    // Preserve scroll anchor when history is prepended: capture height before
    // replaceChildren so we can restore relative position after.
    const prevScrollHeight = this.scroll.scrollHeight
    const prevScrollTop    = this.scroll.scrollTop

    this.scroll.replaceChildren()
    if (this.subjectCard.childNodes.length) this.scroll.append(this.subjectCard)
    if (store.hasMoreHistory) {
      // Sentinel at top — scroll to here triggers load-more via the scroll
      // listener set up by restoreHistory. Shows a subtle loading indicator
      // so the user knows older messages are available.
      const sentinel = el('div', 'ocw-load-more')
      sentinel.textContent = '↑ Loading earlier messages…'
      sentinel.style.pointerEvents = 'none'
      this.scroll.append(sentinel)
    }
    let maxOther = 0
    let prevSender: string | null = null
    const msgs = [...store.messages()]
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i]!
      const showLabel = m.senderRole !== 'system' && m.senderId !== this.me && !m.internal && m.senderId !== prevSender
      this.scroll.append(this.messageEl(m, store, showLabel, i === msgs.length - 1))
      if (m.senderRole !== 'system') prevSender = m.senderId
      if (m.senderId !== this.me && m.seq > maxOther) maxOther = m.seq
    }
    // Auto-scroll to bottom only for new messages; restore anchor when history was prepended.
    if (prevScrollTop > 20) {
      this.scroll.scrollTop = this.scroll.scrollHeight - prevScrollHeight + prevScrollTop
    } else {
      this.scroll.scrollTop = this.scroll.scrollHeight
    }
    if (maxOther > 0) this.h.onReadUpTo(maxOther)

    const typingNames = [...store.typing]
    this.typing.classList.toggle('active', typingNames.length > 0)
    // Bubble is always present in DOM (hidden via CSS); just update label
    const bubble = this.typing.querySelector('.ocw-typing-bubble')
    if (bubble) bubble.setAttribute('aria-label', typingNames.length ? (this.cfg.i18n?.typing ?? 'Typing') : '')
    this.footer.style.display = store.whiteLabel ? 'none' : 'block'

    // Offline mode: show form instead of chat input
    // Pre-chat qualification (dashboard-configured, arrives in the manifest):
    // shown before the FIRST message when enabled — 'offline'-scoped configs
    // replace the default leave-a-message form; 'always' configs gate the
    // composer while the team is online too. Never re-shown once completed
    // or once the conversation has any history.
    // "Before the first message" means the GUEST hasn't spoken — a chatroom
    // welcomeMessage is a real stored system message, so counting ALL
    // messages suppressed pre-chat (and deflection) on exactly the chatrooms
    // most likely to configure them.
    const guestHasSpoken = store.messages().some(m => m.senderRole === 'guest')
    const preChatWanted = !!store.preChat?.enabled && !this.preChatDone && !guestHasSpoken &&
      (store.preChat!.showWhen !== 'offline' || store.offline)
    if (preChatWanted) {
      if (!this.preChatBuilt) this.buildPreChatPanel(store.preChat!)
      this.preChatPanel.style.display = 'block'
      this.awayNotice.style.display = 'none'
      ;(this.root.querySelector('.ocw-input') as HTMLElement | null)?.style.setProperty('display', 'none')
    } else {
      this.preChatPanel.style.display = 'none'
      // Being outside office hours only changes EXPECTATIONS, never capability:
      // say we're away, keep the composer, deliver the message, reply later.
      if (store.offline) {
        if (!this.awayNotice.firstChild) {
          this.awayNotice.append(el('span', 'ocw-away-icon', '🌙'), el('span', '', ''))
        }
        const copy = this.awayNotice.lastChild as HTMLElement
        copy.textContent = store.offlineMessage
          || this.cfg.i18n?.offline
          || "We're away right now — send your message and we'll reply as soon as we're back."
        this.awayNotice.style.display = 'flex'
      } else {
        this.awayNotice.style.display = 'none'
      }
      ;(this.root.querySelector('.ocw-input') as HTMLElement | null)?.style.removeProperty('display')
    }

    // CSAT: show star-rating panel when conversation reaches a terminal state
    // and the user hasn't yet rated. Terminal states are heuristic: 'resolved',
    // 'closed', 'sold', 'issued', 'checked_out'. The panel self-dismisses on submit.
    const terminalStates = ['resolved', 'closed', 'sold', 'issued', 'checked_out']
    if (this.h.onCsat && !this.csatSubmitted && terminalStates.includes(store.state) && store.messages().length > 0) {
      if (this.csatPanel.style.display === 'none') this.buildCsatPanel()
      this.csatPanel.style.display = 'block'
    }

    // Header availability line: green when online, amber when the chatroom is
    // outside office hours. Server-provided offlineMessage (already localized)
    // is preferred for the away text; falls back to the i18n label.
    const isTerminal = terminalStates.includes(store.state)
    if (store.offline) {
      this.headStatus.textContent = `● ${store.offlineMessage || this.cfg.i18n?.away || 'Away'}`
      this.headStatus.className = 'ocw-head-status away'
      this.headStatus.style.display = ''
    } else if (store.conversationId) {
      this.headStatus.textContent = `● ${this.cfg.i18n?.online ?? 'Online'}`
      this.headStatus.className = 'ocw-head-status online'
      this.headStatus.style.display = ''
    } else {
      this.headStatus.style.display = 'none'
    }

    // Resolved: a legible divider in the thread + a one-tap reopen affordance.
    if (isTerminal && store.messages().length > 0) {
      const note = el('div', 'ocw-resolved-note', `— ${this.cfg.i18n?.resolved ?? 'Marked as resolved'} —`)
      this.scroll.append(note)
      this.reopenBtn.style.display = 'block'
    } else {
      this.reopenBtn.style.display = 'none'
    }
  }

  setConnStatus(status: 'connecting' | 'open' | 'reconnecting' | 'error', message?: string): void {
    if (status === 'open') { this.connStatus.style.display = 'none'; return }
    this.connStatus.style.display = ''
    // 'error' is a FATAL, non-transient state (bad token, closed chatroom, or the
    // relay is unreachable after repeated tries) — show a clear reason and don't
    // pretend we're still "connecting…". Anything else is transient.
    const fatal = status === 'error'
    this.connStatus.className = `ocw-conn-status${fatal ? ' err' : status === 'reconnecting' ? ' warn' : ''}`
    this.connStatus.textContent = fatal
      ? `⚠ ${message ?? 'Chat unavailable'}`
      : status === 'reconnecting' ? (message ?? '↻ reconnecting…') : '● connecting…'
  }

  private buildPreChatPanel(cfg: import('./protocol/frames.js').PreChatConfig): void {
    this.preChatBuilt = true
    this.preChatPanel.replaceChildren()
    this.preChatPanel.append(el('div', 'ocw-prechat-title', cfg.title ?? 'Before we start…'))
    const form = el('div', 'ocw-offline-form')
    const inputs: Partial<Record<'name' | 'email' | 'phone', HTMLInputElement>> = {}
    for (const f of cfg.fields ?? ['name', 'email']) {
      const inp = el('input', 'ocw-offline-input') as HTMLInputElement
      inp.type = f === 'email' ? 'email' : f === 'phone' ? 'tel' : 'text'
      inp.placeholder = f === 'name' ? 'Your name' : f === 'email' ? 'Your email' : 'Your phone number'
      inputs[f] = inp
      form.append(inp)
    }
    let topicSel: HTMLSelectElement | null = null
    if (cfg.topics?.length) {
      topicSel = el('select', undefined) as HTMLSelectElement
      const ph = document.createElement('option'); ph.value = ''; ph.textContent = 'What is this about?'; topicSel.append(ph)
      for (const t of cfg.topics) { const o = document.createElement('option'); o.value = t; o.textContent = t; topicSel.append(o) }
      form.append(topicSel)
    }
    let callbackCb: HTMLInputElement | null = null
    let phoneForCb: HTMLInputElement | null = null
    if (cfg.callbackOption) {
      const row = el('label', 'ocw-prechat-cb')
      callbackCb = document.createElement('input'); callbackCb.type = 'checkbox'
      row.append(callbackCb, document.createTextNode('📞 Request a call back'))
      form.append(row)
      if (!inputs.phone) {
        phoneForCb = el('input', 'ocw-offline-input') as HTMLInputElement
        phoneForCb.type = 'tel'; phoneForCb.placeholder = 'Phone number for the call'; phoneForCb.style.display = 'none'
        callbackCb.addEventListener('change', () => phoneForCb!.style.setProperty('display', callbackCb!.checked ? 'block' : 'none'))
        form.append(phoneForCb)
      }
    }
    const submit = el('button', 'ocw-offline-submit', 'Start chat') as HTMLButtonElement
    submit.type = 'button'
    submit.addEventListener('click', () => {
      const email = inputs.email?.value.trim()
      if (inputs.email && (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { inputs.email.focus(); return }
      const callback = !!callbackCb?.checked
      const phone = (inputs.phone?.value ?? phoneForCb?.value ?? '').trim()
      if (callback && !phone) { (inputs.phone ?? phoneForCb)?.focus(); return }
      if (topicSel && cfg.topics?.length && !topicSel.value) { topicSel.focus(); return }
      submit.disabled = true   // a double-click must not qualify twice
      this.completePreChat()
      this.h.onPreChat?.({
        ...(inputs.name?.value.trim() ? { name: inputs.name.value.trim() } : {}),
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ...(topicSel?.value ? { topic: topicSel.value } : {}),
        ...(callback ? { callback: true } : {}),
      })
    })
    form.append(submit)
    this.preChatPanel.append(form)
  }

  /** Mark pre-chat complete (submitted now or in a previous session). */
  completePreChat(): void {
    this.preChatDone = true
    this.preChatPanel.style.display = 'none'
    if (this.storeRef) this.render(this.storeRef)
  }

  /** KB deflection results ("was this your question?") above the composer. */
  showDeflection(articles: { id: string; title: string; answer: string }[]): void {
    if (!articles.length) return this.hideDeflection()
    this.deflectPanel.replaceChildren()
    this.deflectPanel.append(el('div', 'ocw-deflect-hint', 'Instant answers — tap to expand'))
    for (const a of articles.slice(0, 3)) {
      const card = el('button', 'ocw-deflect-card')
      card.append(el('div', 'ocw-deflect-q', a.title), el('div', 'ocw-deflect-a', a.answer))
      card.addEventListener('click', () => card.classList.toggle('open'))
      this.deflectPanel.append(card)
    }
    this.deflectPanel.style.display = 'flex'
  }

  hideDeflection(): void {
    this.deflectPanel.style.display = 'none'
    this.deflectPanel.replaceChildren()
  }

  private buildCsatPanel(): void {
    this.csatPanel.replaceChildren()
    this.csatPanel.append(el('div', 'ocw-csat-title', 'How did we do?'))
    const stars = el('div', 'ocw-csat-stars')
    const btns: HTMLButtonElement[] = []
    for (let i = 1; i <= 5; i++) {
      const b = el('button', 'ocw-csat-star', '★')
      b.dataset['score'] = String(i)
      b.addEventListener('mouseenter', () => btns.forEach((bb, idx) => bb.classList.toggle('lit', idx < i)))
      b.addEventListener('mouseleave', () => btns.forEach(bb => bb.classList.remove('lit')))
      b.addEventListener('click', () => {
        this.csatSubmitted = true
        this.csatPanel.replaceChildren(el('div', 'ocw-csat-done', `Thanks for your ${i}★ rating!`))
        this.h.onCsat?.(i)
      })
      btns.push(b); stars.append(b)
    }
    this.csatPanel.append(stars)
  }

  /** Whether the subject card (server Subject entity, mount config fallback)
   *  has been built — built once when data first arrives. */
  private subjectBuilt = false

  private buildSubjectCard(store: ChatStore): void {
    if (this.subjectBuilt) return
    const s = store.subject
    const cfg = this.cfg.subject
    // Only show the subject card when there is actual subject data (from the
    // server) or an explicit subject config passed by the embedder (title,
    // tags, status). Never fall back to store.name — that's the domain/profile
    // name and is already shown in the header; rendering it here as well is
    // what caused the duplication seen in the Hotel front desk screenshot.
    const title = s?.title ?? cfg?.title
    if (!title) return
    this.subjectBuilt = true
    this.subjectCard.replaceChildren()
    this.subjectCard.append(el('div', 'ocw-subject-title', title))
    if (cfg?.subtitle) this.subjectCard.append(el('div', 'ocw-subject-sub', cfg.subtitle))
    const tags = el('div', 'ocw-tags')
    if (s) for (const [k, v] of Object.entries(s.fields)) tags.append(el('span', 'ocw-tag', `${k}: ${v}`))
    else for (const t of cfg?.tags ?? []) tags.append(el('span', 'ocw-tag', t))
    if (tags.childNodes.length) this.subjectCard.append(tags)
    const status = s?.state ?? cfg?.status
    if (status) { this.statusBadge.textContent = status; this.statusBadge.style.display = 'inline-flex' }
  }

  private chipEl(a: ManifestAction): HTMLButtonElement {
    const btn = el('button', 'ocw-chip', a.icon ? `${a.icon} ${a.label}` : a.label)
    btn.dataset['actionId'] = a.id
    btn.addEventListener('click', async () => {
      if (a.confirm && !(await this.confirm(a.label))) return
      if (a.input?.length) this.openForm(a)
      else this.h.onInvoke(a.id)
    })
    return btn
  }

  /** In-widget confirmation modal (replaces window.confirm). */
  private confirm(label: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = el('div', 'ocw-modal')
      const card = el('div', 'ocw-modal-card')
      card.append(el('div', 'ocw-modal-title', label))
      card.append(el('div', 'ocw-modal-body', `Confirm “${label}”?`))
      const row = el('div', 'ocw-modal-actions')
      const cancel = el('button', 'ocw-modal-cancel', 'Cancel')
      const ok = el('button', 'ocw-modal-ok', 'Confirm')
      const close = (v: boolean) => { overlay.remove(); resolve(v) }
      cancel.addEventListener('click', () => close(false))
      ok.addEventListener('click', () => close(true))
      overlay.addEventListener('click', (e) => { if (e.target === overlay) close(false) })
      row.append(cancel, ok); card.append(row); overlay.append(card)
      this.root.append(overlay)
      ok.focus()
    })
  }

  /** Inline form for a form-effect action: typed inputs (date picker, number,
   *  text) rendered above the composer — no browser prompts. `prefill` seeds
   *  field values (search pre-fill) — deterministic parse, correctable before
   *  submit. */
  private openForm(a: ManifestAction, prefill?: Record<string, string>): void {
    this.formHost.replaceChildren()
    const panel = el('div', 'ocw-form')
    panel.append(el('div', 'ocw-form-title', a.icon ? `${a.icon} ${a.label}` : a.label))
    const inputs = new Map<string, HTMLInputElement>()
    for (const f of a.input ?? []) {
      const pre = prefill?.[f.name]
      const row = el('label', 'ocw-form-row'); row.append(el('span', 'ocw-form-lbl', f.label))
      if (f.type === 'select' && f.options?.length) {
        const sel = el('select', 'ocw-form-input')
        if (!f.required) sel.append(el('option', undefined, '— select —'))
        for (const opt of f.options) { const o = el('option'); o.value = opt; o.textContent = opt; sel.append(o) }
        if (f.required) sel.required = true
        if (pre != null && f.options.includes(pre)) sel.value = pre
        row.append(sel)
        inputs.set(f.name, sel as unknown as HTMLInputElement)
      } else {
        const inp = el('input', 'ocw-form-input')
        inp.type = f.type === 'number' ? 'number' : f.type === 'date' ? 'datetime-local' : 'text'
        if (f.required) inp.required = true
        if (pre != null && pre !== '') inp.value = pre
        row.append(inp); inputs.set(f.name, inp)
      }
      panel.append(row)
    }
    const actions = el('div', 'ocw-form-actions')
    const cancel = el('button', 'ocw-form-cancel', 'Cancel')
    const submit = el('button', 'ocw-form-submit', 'Send')
    cancel.addEventListener('click', () => this.formHost.replaceChildren())
    submit.addEventListener('click', () => {
      const out: Record<string, unknown> = {}
      for (const [name, inp] of inputs) {
        if (inp.required && !inp.value) { inp.style.borderColor = '#e5484d'; return }
        out[name] = inp.type === 'number' ? Number(inp.value) : inp.value
      }
      this.formHost.replaceChildren()
      this.h.onInvoke(a.id, out)
    })
    actions.append(cancel, submit); panel.append(actions)
    this.formHost.append(panel)
    inputs.values().next().value?.focus()
  }

  private messageEl(m: RenderMessage, store: ChatStore, showLabel = false, isLast = false): HTMLElement {
    if (m.senderRole === 'system') {
      const sys = el('div', 'ocw-sys')
      const original = m.deletedAt ? 'message deleted' : contentText(m.content)
      sys.textContent = original
      // Fixed/operator system messages (welcome, notices) auto-translate to the
      // guest's language — no click needed. Fetched once, cached, and the
      // original is kept as a hover tooltip. Off if translation is unavailable.
      if (!m.deletedAt && this.h.onTranslate && m.content.kind === 'text' && original.trim()) {
        const cached = this.translationCache.get(m.id)
        if (cached !== undefined) {
          if (cached !== original) { sys.textContent = cached; sys.title = original }
        } else if (!this.autoTranslated.has(m.id)) {
          this.autoTranslated.add(m.id)
          void this.h.onTranslate(original).then((res) => {
            if (res && res.trim() && res !== original) { this.translationCache.set(m.id, res); sys.textContent = res; sys.title = original }
          })
        }
      }
      return sys
    }
    const mine = m.senderId === this.me
    const isNote = !!m.internal
    const row = el('div', `ocw-row ${isNote ? 'ocw-note mine' : mine ? 'mine' : 'theirs'} ${m.senderRole === 'bot' ? 'ocw-bot' : ''}`)
    const col = el('div')
    if (showLabel) {
      const who = m.senderRole === 'bot'
        ? (this.cfg.i18n?.aiAssistant ?? 'AI Assistant')
        : (this.cfg.subject?.ownerLabel ?? this.cfg.subject?.title ?? 'Support')
      col.append(el('div', 'ocw-msg-sender', who))
    }
    const bubbleWrap = el('div', 'ocw-bubble-wrap')
    // Reply-to context if present
    if (m.replyToId) {
      const replyCtx = el('div', 'ocw-reply-to', '↩ replying to a message')
      replyCtx.style.cssText = 'font-size:11px;color:var(--ocw-mut);margin-bottom:2px;font-style:italic'
      col.append(replyCtx)
    }
    const bubble = el('div', 'ocw-bubble')
    let textNode: Text | null = null
    if (m.deletedAt) bubble.append(el('span', 'ocw-deleted', 'message deleted'))
    else if (m.content.kind === 'attachment') {
      const c = m.content
      // Media may also be inlined as a data: URI by an upload pipeline, which
      // is safe for img/video src (they cannot execute) but never for a link.
      const mediaSrc = safeUrl(c.url, { allowData: /^data:(image|video)\// })
      const linkHref = safeUrl(c.url)
      if (c.mime?.startsWith('image/') && mediaSrc) {
        const img = document.createElement('img')
        img.src = mediaSrc; img.alt = c.name ?? 'image'
        img.style.cssText = 'max-width:220px;max-height:160px;border-radius:10px;display:block;cursor:pointer'
        if (linkHref) {
          img.addEventListener('click', () => window.open(linkHref, '_blank', 'noopener,noreferrer'))
        } else {
          img.style.cursor = 'default'
        }
        bubble.append(img)
      } else if (c.mime?.startsWith('video/') && mediaSrc) {
        const video = document.createElement('video')
        video.src = mediaSrc; video.controls = true
        video.style.cssText = 'max-width:240px;max-height:180px;border-radius:10px;display:block'
        bubble.append(video)
      } else if (linkHref) {
        const a = document.createElement('a')
        a.href = linkHref; a.target = '_blank'; a.rel = 'noopener noreferrer'
        a.style.cssText = 'display:flex;align-items:center;gap:8px;color:inherit;text-decoration:none'
        a.append(el('span', undefined, '📄'), el('span', undefined, c.name ?? 'file'))
        bubble.append(a)
      } else {
        // Unrenderable/unsafe URL — show the filename, never a live link.
        bubble.append(el('span', undefined, `📄 ${c.name ?? 'file'}`))
      }
    } else {
      if (m.content.kind === 'products') {
        // Horizontal strip of product cards. Every image src and link href goes
        // through safeUrl — the server validated them, but the widget renders in
        // the customer's origin and must not depend on that.
        const strip = el('div', 'ocw-products')
        for (const it of m.content.items.slice(0, 6)) {
          const href = safeUrl(it.url)
          const card = document.createElement(href ? 'a' : 'div')
          card.className = 'ocw-product'
          if (href && card instanceof HTMLAnchorElement) { card.href = href; card.target = '_blank'; card.rel = 'noopener noreferrer' }
          const img = safeUrl(it.image, { allowData: /^data:image\// })
          if (img) { const im = document.createElement('img'); im.src = img; im.alt = ''; im.loading = 'lazy'; im.className = 'ocw-product-img'; card.append(im) }
          card.append(el('div', 'ocw-product-title', it.title))
          if (it.subtitle) card.append(el('div', 'ocw-product-sub', it.subtitle))
          if (it.price) card.append(el('div', 'ocw-product-price', it.price))
          strip.append(card)
        }
        if (m.content.title) bubble.append(el('div', 'ocw-products-title', m.content.title))
        bubble.append(strip)
      } else if (m.content.kind === 'appointment') {
        const ap = m.content
        const card = el('div', 'ocw-appt')
        card.append(el('div', 'ocw-appt-title', `\u{1F4C5} ${ap.title}`))
        card.append(el('div', 'ocw-appt-time', new Date(ap.startIso).toLocaleString() + ' \u2013 ' + new Date(ap.endIso).toLocaleTimeString()))
        if (ap.location) card.append(el('div', 'ocw-appt-loc', `\u{1F4CD} ${ap.location}`))
        if (ap.description) card.append(el('div', 'ocw-appt-desc', ap.description))
        const links = el('div', 'ocw-appt-links')
        const gHref = safeUrl(ap.googleUrl)
        const iHref = safeUrl(ap.icalUrl, { allowData: /^data:text\/calendar[;,]/i })
        if (gHref) {
          const gLink = document.createElement('a'); gLink.href = gHref; gLink.target = '_blank'; gLink.rel = 'noopener noreferrer'; gLink.className = 'ocw-appt-btn'; gLink.textContent = '\u{1F4C5} Add to Google Calendar'
          links.append(gLink)
        }
        if (iHref) {
          const iLink = document.createElement('a'); iLink.href = iHref; iLink.download = `${ap.title}.ics`; iLink.className = 'ocw-appt-btn ocw-appt-btn-sec'; iLink.textContent = '\u{1F34E} Apple / iCal'
          links.append(iLink)
        }
        // The appointment card still renders without its links; the details are
        // the point, and a dropped button is better than a live bad URL.
        if (links.childNodes.length) card.append(links)
        bubble.append(card)
      } else {
        textNode = linkifyInto(bubble, contentText(m.content))
        if (m.editedAt) bubble.append(el('span', 'ocw-edited', '(edited)'))
      }
    }
    bubbleWrap.append(bubble)

    // Guided-flow choices: a prompt message carries tappable options. Only the
    // LATEST message's chips are live — older prompts (on reload / after the guest
    // has answered) must not re-fire, so gate on isLast. Tapping sends the label
    // as a guest turn (reusing onSend), which the server-side flow then consumes.
    if (isLast && !m.deletedAt && m.content.kind === 'text' && m.content.quickReplies?.length) {
      const chips = el('div', 'ocw-flow-choices')
      chips.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;margin-top:6px'
      for (const label of m.content.quickReplies) {
        const b = el('button', 'ocw-flow-choice', label)
        b.type = 'button'
        b.style.cssText = 'border:1px solid var(--ocw-bd,rgba(0,0,0,.12));background:var(--ocw-bg,#fff);color:inherit;border-radius:14px;padding:5px 12px;font:inherit;font-size:13px;cursor:pointer'
        b.addEventListener('click', () => {
          chips.querySelectorAll('button').forEach(x => ((x as HTMLButtonElement).disabled = true))
          chips.style.opacity = '0.5'
          this.h.onSend(label)
        })
        chips.append(b)
      }
      bubbleWrap.append(chips)
    }

    // Search pre-fill (Search Phase 2/3): a `form`-kind message points at an
    // action + optional parsed `prefill`. Render a button that opens that
    // action's form seeded with the parsed values — tap to review, not auto-run.
    // Gated on isLast so an old suggestion on reload doesn't re-offer stale
    // parses; the action chip still opens the form blank at any time.
    if (isLast && !m.deletedAt && m.content.kind === 'form') {
      const c = m.content
      const a = store.actionById(c.actionId)
      if (a?.input?.length) {
        const cta = el('button', 'ocw-chip', a.icon ? `${a.icon} ${a.label}` : a.label)
        cta.type = 'button'
        cta.style.cssText = 'margin-top:6px'
        cta.addEventListener('click', async () => {
          if (a.confirm && !(await this.confirm(a.label))) return
          this.openForm(a, c.prefill)
        })
        bubbleWrap.append(cta)
      }
    }

    // Live translation: only for the other party's plain-text messages (not notes).
    // The translation renders as its own line directly AFTER the original text —
    // both stay visible, the message is never overwritten — toggled by a button.
    if (!mine && !isNote && this.h.onTranslate && m.content.kind === 'text' && !m.deletedAt && m.seq > 0 && textNode) {
      const original = m.content.text
      if (original.trim()) {
        const translateBtn = el('button', 'ocw-translate-btn')
        translateBtn.type = 'button'
        const tL = {
          translate: this.cfg.i18n?.translate ?? 'Translate',
          original: this.cfg.i18n?.original ?? 'Original',
          showOriginal: this.cfg.i18n?.showOriginal ?? 'Show original',
          unavailable: this.cfg.i18n?.translateUnavailable ?? 'Unavailable',
        }
        // Build with textContent (host i18n overrides are untrusted → never innerHTML).
        const paintBtn = (icon: string, label: string, title: string): void => {
          translateBtn.textContent = ''
          const ic = el('span', ''); ic.setAttribute('aria-hidden', 'true'); ic.textContent = icon
          translateBtn.append(ic, document.createTextNode(' ' + label))
          translateBtn.title = title
        }
        paintBtn('🌐', tL.translate, tL.translate)
        const transLine = el('div', 'ocw-translation')
        transLine.style.display = 'none'
        bubble.append(transLine)

        const showLine = (text: string): void => {
          transLine.textContent = text
          transLine.style.display = ''
          this.showingTranslation.add(m.id)
          paintBtn('↩', tL.original, tL.showOriginal)
        }
        const hideLine = (): void => {
          transLine.style.display = 'none'
          this.showingTranslation.delete(m.id)
          paintBtn('🌐', tL.translate, tL.translate)
        }

        translateBtn.addEventListener('click', (e) => {
          e.stopPropagation()
          if (this.showingTranslation.has(m.id)) { hideLine(); return }
          const cached = this.translationCache.get(m.id)
          if (cached !== undefined) { showLine(cached); return }
          translateBtn.textContent = '⏳ …'
          void this.h.onTranslate!(original).then((result) => {
            if (result === null) {
              translateBtn.textContent = '⚠️ ' + tL.unavailable
              setTimeout(() => hideLine(), 1500)
              return
            }
            this.translationCache.set(m.id, result)
            showLine(result)
          })
        })
        bubbleWrap.append(translateBtn)

        // Keep a shown translation visible across re-renders.
        const cached = this.translationCache.get(m.id)
        if (this.showingTranslation.has(m.id) && cached !== undefined) showLine(cached)
      }
    }
    // Edit/delete context menu on own non-deleted messages
    if (mine && !m.deletedAt && m.seq > 0 && (this.h.onEdit ?? this.h.onDelete)) {
      const menu = el('div', 'ocw-msg-menu')
      if (this.h.onEdit) {
        const editBtn = el('button', undefined, '✏️')
        editBtn.title = 'Edit'
        editBtn.addEventListener('click', (e) => {
          e.stopPropagation()
          // Inline edit: replace bubble text with a small textarea + save/cancel
          const original = contentText(m.content)
          const ta = document.createElement('textarea')
          ta.value = original
          ta.rows = Math.min(4, Math.ceil(original.length / 40) + 1)
          ta.style.cssText = 'width:100%;resize:vertical;border:1px solid var(--ocw-accent);border-radius:8px;padding:6px 10px;font:inherit;font-size:14px;background:var(--ocw-card);color:#1c1b1a;box-sizing:border-box'
          const saveBtn = el('button', 'ocw-form-submit', 'Save')
          saveBtn.style.cssText = 'margin-top:6px;padding:5px 14px;font-size:13px'
          const cancelBtn = el('button', 'ocw-form-cancel', 'Cancel')
          cancelBtn.style.cssText = 'margin-top:6px;padding:5px 10px;font-size:13px'
          const btnRow = el('div'); btnRow.style.cssText = 'display:flex;gap:6px;justify-content:flex-end'
          btnRow.append(cancelBtn, saveBtn)
          const editPanel = el('div'); editPanel.append(ta, btnRow)
          bubble.replaceChildren(editPanel)
          ta.focus(); ta.select()
          const restore = () => bubble.replaceChildren(textNode ?? document.createTextNode(original))
          cancelBtn.addEventListener('click', restore)
          saveBtn.addEventListener('click', () => {
            const newText = ta.value.trim()
            if (newText && newText !== original) { this.h.onEdit!(m.id, newText); restore() }
            else restore()
          })
          ta.addEventListener('keydown', (ke) => {
            if (ke.key === 'Enter' && !ke.shiftKey) { ke.preventDefault(); saveBtn.click() }
            if (ke.key === 'Escape') restore()
          })
        })
        menu.append(editBtn)
      }
      if (this.h.onDelete) {
        const delBtn = el('button', 'del', '🗑')
        delBtn.title = 'Delete'
        delBtn.addEventListener('click', (e) => { e.stopPropagation(); this.h.onDelete!(m.id) })
        menu.append(delBtn)
      }
      bubbleWrap.append(menu)
    }
    col.append(bubbleWrap)

    // A3.3 — bot answer affordances: KB source citations + 👍/👎 feedback. Only on
    // the bot's own answers (not human/system), non-deleted, persisted messages.
    if (m.senderRole === 'bot' && !m.deletedAt && m.seq > 0 && m.content.kind === 'text') {
      const cites = m.content.citations
      if (cites?.length) {
        const src = el('div', 'ocw-cites')
        src.style.cssText = 'margin-top:4px;font-size:11px;color:var(--ocw-mut,#667)'
        src.textContent = `${this.cfg.i18n?.sources ?? 'Sources'}: ${cites.map(c => c.source).join(', ')}`
        col.append(src)
      }
      if (this.h.onReact) {
        const fb = el('div', 'ocw-bot-feedback')
        fb.style.cssText = 'display:flex;gap:6px;margin-top:4px'
        const mk = (emoji: string, label: string) => {
          const b = el('button', 'ocw-fb-btn', emoji)
          b.type = 'button'; b.title = label
          b.style.cssText = 'border:1px solid var(--ocw-bd,rgba(0,0,0,.12));background:transparent;border-radius:12px;padding:1px 8px;font-size:12px;cursor:pointer;opacity:.7'
          const already = () => !!m.reactions?.[emoji]?.includes(this.me as never)
          if (already()) b.style.opacity = '1'
          b.addEventListener('click', () => { this.h.onReact?.(m.id, emoji, already()); b.style.opacity = '1' })
          return b
        }
        fb.append(mk('👍', this.cfg.i18n?.helpful ?? 'Helpful'), mk('👎', this.cfg.i18n?.notHelpful ?? 'Not helpful'))
        col.append(fb)
      }
    }

    if (this.h.onReact && !m.deletedAt && m.seq > 0) {
      const reactWrap = el('div', 'ocw-react-wrap')
      const reactRow = el('div', 'ocw-react')
      // Existing reaction pills
      if (m.reactions && Object.keys(m.reactions).length) {
        for (const [emoji, users] of Object.entries(m.reactions)) {
          const pill = el('button', `ocw-react-pill${(users as string[]).includes(this.me) ? ' mine' : ''}`, `${emoji} ${(users as string[]).length}`)
          pill.addEventListener('click', () => this.h.onReact?.(m.id, emoji, (users as string[]).includes(this.me)))
          reactRow.append(pill)
        }
      }
      // Add-reaction button + picker
      const addBtn = el('button', 'ocw-react-btn', '＋')
      const picker = el('div', 'ocw-react-picker')
      for (const emoji of REACTION_EMOJIS) {
        const pb = el('button', undefined, emoji)
        pb.addEventListener('click', (e) => {
          e.stopPropagation()
          const alreadyReacted = m.reactions?.[emoji]?.includes(this.me as never)
          this.h.onReact?.(m.id, emoji, !!alreadyReacted)
          picker.style.display = 'none'
        })
        picker.append(pb)
      }
      picker.style.display = 'none'
      addBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        const opening = picker.style.display === 'none'
        picker.style.display = opening ? 'flex' : 'none'
        // Close on the next outside click. Registered only when OPENING —
        // the old code added one document listener per message on EVERY
        // render, so long conversations piled up hundreds of stale handlers.
        if (opening) document.addEventListener('click', () => { picker.style.display = 'none' }, { once: true })
      })
      reactRow.append(addBtn)
      reactWrap.append(reactRow, picker)
      col.append(reactWrap)
    } else if (m.reactions && Object.keys(m.reactions).length) {
      col.append(el('div', 'ocw-react', Object.entries(m.reactions).map(([e, u]) => `${e}${(u as string[]).length}`).join(' ')))
    }
    const meta = el('div', 'ocw-time ocw-meta', fmtTime(m.ts))
    if (mine && m.status) {
      const t = el('span', `ocw-tick${m.status === 'read' ? ' read' : m.status === 'delivered' ? ' delivered' : ''}`, tick(m.status))
      meta.append(t)
    }
    // "Seen" indicator when agent has read past this message
    if (mine && m.seq > 0 && store.lastReadByOthers >= m.seq) {
      meta.append(el('span', 'ocw-seen', ' · Seen'))
    }
    col.append(meta)
    row.append(col)
    return row
  }
}

function tick(s: NonNullable<RenderMessage['status']>): string {
  switch (s) {
    case 'read':      return '✓✓'   // blue double tick rendered via CSS colour
    case 'delivered': return '✓✓'
    case 'sent':      return '✓'
    default:          return '🕓'
  }
}
